import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ExternalLink } from 'lucide-react';

interface EmailEventDetailModalProps {
  event: any;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const EmailEventDetailModal = ({ event, open, onOpenChange }: EmailEventDetailModalProps) => {
  if (!event) {
    return null;
  }

  const getStatusColor = (eventType: string) => {
    switch (eventType) {
      case 'EMAIL_SENT':
        return 'bg-green-100 text-green-800';
      case 'EMAIL_OPENED':
        return 'bg-blue-100 text-blue-800';
      case 'EMAIL_REPLIED':
      case 'EMAIL_REPLY':
        return 'bg-purple-100 text-purple-800';
      case 'EMAIL_BOUNCED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (date: string) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="text-xl">
              Email Event Details
              {event.sequence_number && (
                <span className="ml-2 text-sm text-muted-foreground">
                  (Sequence #{event.sequence_number})
                </span>
              )}
            </DialogTitle>
            <div className="flex items-center gap-2">
              <Badge className={getStatusColor(event.event_type)}>
                {event.event_type || 'EMAIL_SENT'}
              </Badge>
              {event.app_url && (
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => window.open(event.app_url, '_blank')}
                >
                  <ExternalLink className="h-4 w-4 mr-1" />
                  View in Smartlead
                </Button>
              )}
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">From</label>
              <div className="font-mono text-sm">{event.from_email || 'N/A'}</div>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">To</label>
              <div className="font-mono text-sm">{event.to_email || 'N/A'}</div>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Subject</label>
              <div className="font-medium">{event.subject || 'N/A'}</div>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Campaign</label>
              <div className="font-medium">{event.campaign_name || 'N/A'}</div>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Timestamp</label>
              <div className="font-mono text-sm">{formatDate(event.event_timestamp || event.created_at)}</div>
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Lead ID</label>
              <div className="font-mono text-sm">{event.sl_email_lead_id || 'N/A'}</div>
            </div>
          </div>

          <div className="p-4 bg-muted rounded-lg">
            <p className="text-sm text-muted-foreground">
              <strong>Note:</strong> This is a simplified view. The full Email Activity feature has been archived. 
              For detailed email event information, please contact your administrator.
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
